// WHAT: Settings view component for Parqet PDF extraction configuration.
// WHY:  Provides interface for configuring PDF extraction parameters (concurrent extractions, timeout) to optimize batch processing performance.
// DOMAIN: Investments → Parqet
// DEPENDENCIES: React, @tauri-apps/api, @/components/ui, lucide-react
// RISKS:
//   - WARN: Tauri commands 'load_parqet_settings' and 'save_parqet_settings' must exist in backend; failures will show error messages.
//   - NOTE: Settings validation uses hardcoded ranges (1-20 for concurrent, 5-120 for timeout); ensure these match backend constraints.
//   - WARN: Default settings are hardcoded; changes require code updates.

import { core } from "@tauri-apps/api";
import { FileText, Settings, CheckCircle, AlertCircle, Loader2, Info, Save, X } from "lucide-react";
import { useState, useEffect } from "react";

import { Card, Button, Input, Label, Stack, Heading } from "@/components/ui";

interface ParqetSettings {
  extraction_timeout_secs: number;
  max_concurrent_pdf_extractions: number;
}

/**
 * WHAT:
 *   Settings view component for Parqet PDF extraction configuration.
 *
 * WHY:
 *   Provides users with interface for configuring PDF extraction parameters to optimize
 *   batch processing performance. Allows adjustment of concurrent extraction limits and
 *   timeout values based on system resources and PDF complexity.
 *
 * BEHAVIOR:
 *   - Loads settings from backend on mount using 'load_parqet_settings' Tauri command
 *   - Displays loading state during settings fetch
 *   - Provides input fields for max_concurrent_pdf_extractions (1-20) and extraction_timeout_secs (5-120)
 *   - Validates input ranges before saving
 *   - Saves settings to backend using 'save_parqet_settings' Tauri command
 *   - Shows success/error messages after save operations
 *   - Supports resetting to default values
 *   - Displays configuration status and information cards
 *
 * RISKS:
 *   - WARN: Tauri commands 'load_parqet_settings' and 'save_parqet_settings' must exist in backend; failures will show error messages.
 *   - NOTE: Settings validation uses hardcoded ranges (1-20 for concurrent, 5-120 for timeout); ensure these match backend constraints.
 *   - WARN: Default settings are hardcoded; changes require code updates.
 *   - NOTE: Error handling falls back to default settings; users may not notice load failures.
 *
 * @returns {JSX.Element} The ParqetSettingsView component
 *
 */
const ParqetSettingsView = () => {
  const [settings, setSettings] = useState<ParqetSettings>({
    extraction_timeout_secs: 15,
    max_concurrent_pdf_extractions: 4,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [success, setSuccess] = useState<null | string>(null);

  /**
   * WHAT:
   *   Effect hook to load settings on component mount.
   *
   * WHY:
   *   Ensures settings are displayed immediately when component is rendered.
   *
   * SIDE EFFECTS:
   *   - Calls loadSettings to fetch settings from backend
   */
  useEffect(() => {
    void loadSettings();
  }, []);

  /**
   * WHAT:
   *   Loads Parqet settings from backend.
   *
   * WHY:
   *   Retrieves current configuration to display in the UI.
   *
   * SIDE EFFECTS:
   *   - Sets loading state to true during fetch
   *   - Invokes 'load_parqet_settings' Tauri command
   *   - Updates settings state with loaded values
   *   - Sets error state if loading fails
   *   - Sets loading state to false on completion
   *
   * RISKS:
   *   - WARN: Falls back to default settings on error; users may not notice load failures.
   *   - NOTE: Error message is generic; detailed error information is logged to console.
   */
  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const loadedSettings = await core.invoke<ParqetSettings>("load_parqet_settings");
      setSettings(loadedSettings);
    } catch {
      setError("Failed to load settings. Using defaults.");
      // NOTE: Error loading Parqet settings - handled by state
    } finally {
      setLoading(false);
    }
  };

  /**
   * WHAT:
   *   Validates and saves Parqet settings to backend.
   *
   * WHY:
   *   Persists user-configured extraction parameters for future batch processing operations.
   *
   * SIDE EFFECTS:
   *   - Validates settings ranges before saving
   *   - Sets saving state to true during operation
   *   - Clears error and success states
   *   - Invokes 'save_parqet_settings' Tauri command
   *   - Sets success message on successful save
   *   - Sets error message if validation fails or save fails
   *   - Sets saving state to false on completion
   *
   * RISKS:
   *   - WARN: Validation ranges are hardcoded; ensure they match backend constraints.
   *   - NOTE: Error messages are user-friendly but may not provide specific failure reasons.
   *   - WARN: Settings are saved immediately; no confirmation dialog for changes.
   */
  const handleSave = async () => {
    // NOTE: Validation with hardcoded ranges
    if (
      settings.max_concurrent_pdf_extractions < 1 ||
      settings.max_concurrent_pdf_extractions > 20
    ) {
      setError("Max concurrent PDF extractions must be between 1 and 20");
      return;
    }
    if (settings.extraction_timeout_secs < 5 || settings.extraction_timeout_secs > 120) {
      setError("Extraction timeout must be between 5 and 120 seconds");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await core.invoke("save_parqet_settings", { settings });
      setSuccess("✅ Settings saved successfully!");
    } catch {
      setError("❌ Error saving settings.");
      // NOTE: Error saving Parqet settings - handled by state
    } finally {
      setSaving(false);
    }
  };

  /**
   * WHAT:
   *   Resets settings to default values.
   *
   * WHY:
   *   Provides quick way to restore factory defaults when configuration becomes misconfigured.
   *
   * SIDE EFFECTS:
   *   - Sets settings to default values (4 concurrent, 15 seconds timeout)
   *   - Clears error and success states
   *
   * RISKS:
   *   - WARN: This action cannot be undone; all custom settings will be lost.
   *   - NOTE: Default values are hardcoded; changes require code updates.
   */
  const handleReset = () => {
    setSettings({
      extraction_timeout_secs: 15,
      max_concurrent_pdf_extractions: 4,
    });
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="bg-slate-800/30 border-slate-700/50" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={4}>
          <div className="p-3 bg-tracq-bright-blue/10 rounded-lg">
            <FileText className="w-6 h-6 text-tracq-bright-blue" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading className="text-xl font-semibold text-white" level={2}>
              Parqet PDF Extraction Settings
            </Heading>
            <p className="text-slate-300">
              Configure PDF extraction behavior for batch processing and performance optimization.
            </p>
          </Stack>
        </Stack>
      </Card>

      {/* Configuration Form */}
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={4}>
          {/* Header */}
          <Stack align="center" direction="row" gap={3}>
            <div className="p-2 bg-tracq-bright-blue/10 rounded-lg">
              <Settings className="w-5 h-5 text-tracq-bright-blue" />
            </div>
            <Heading className="text-lg font-medium text-white" level={3}>
              Extraction Configuration
            </Heading>
          </Stack>

          {/* Max Concurrent PDF Extractions */}
          <Stack direction="col" gap={2}>
            <Label className="text-slate-300">
              Max Concurrent PDF Extractions
              <span className="text-red-400 ml-1">*</span>
            </Label>
            <Input
              className="w-full"
              disabled={loading || saving}
              max="20"
              min="1"
              onChange={(e) => {
                setSettings({
                  ...settings,
                  max_concurrent_pdf_extractions: Number.parseInt(e.target.value) || 1,
                });
              }}
              size="md"
              type="number"
              value={settings.max_concurrent_pdf_extractions}
            />
            <p className="text-slate-500 text-xs">
              Maximum number of PDFs processed simultaneously (1-20). Lower values reduce memory
              usage and prevent &quot;Invalid file header&quot; errors during batch processing.
              Recommended: 4-6.
            </p>
          </Stack>

          {/* Extraction Timeout */}
          <Stack direction="col" gap={2}>
            <Label className="text-slate-300">
              Extraction Timeout (seconds)
              <span className="text-red-400 ml-1">*</span>
            </Label>
            <Input
              className="w-full"
              disabled={loading || saving}
              max="120"
              min="5"
              onChange={(e) => {
                setSettings({
                  ...settings,
                  extraction_timeout_secs: Number.parseInt(e.target.value) || 5,
                });
              }}
              size="md"
              type="number"
              value={settings.extraction_timeout_secs}
            />
            <p className="text-slate-500 text-xs">
              Maximum time to wait for PDF extraction before timing out (5-120 seconds). Prevents
              hangs with corrupted PDFs. Recommended: 15-30 seconds.
            </p>
          </Stack>

          {/* Action Buttons */}
          <Stack className="flex-wrap" direction="row" gap={2}>
            <Button
              disabled={loading || saving}
              onClick={() => {
                void handleSave();
              }}
              size="md"
              variant="primary"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>

            <Button disabled={saving} onClick={handleReset} size="md" variant="ghost">
              <X className="w-4 h-4 mr-2" />
              Reset to Defaults
            </Button>
          </Stack>
        </Stack>
      </Card>

      {/* Information Cards */}
      <Card className="bg-blue-500/10 border-blue-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-400 mb-1">How It Works</h3>
            <p className="text-sm text-blue-300 mb-2">
              These settings control PDF extraction during batch processing (folder scan):
            </p>
            <ul className="text-sm text-blue-300 space-y-1 list-disc list-inside">
              <li>
                <strong>Max Concurrent Extractions:</strong> Limits parallel PDF extractions to
                prevent memory pressure and &quot;Invalid file header&quot; errors. Lower values =
                safer, higher values = faster.
              </li>
              <li>
                <strong>Extraction Timeout:</strong> Maximum time to wait for a single PDF
                extraction. Prevents hangs with corrupted or extremely large PDFs.
              </li>
            </ul>
          </div>
        </Stack>
      </Card>

      <Card className="bg-slate-800/20 border-slate-700/30" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Info className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-1">Tuning Guidelines</h3>
            <ul className="text-sm text-slate-400 space-y-1 list-disc list-inside">
              <li>
                <strong>If you see &quot;Invalid file header&quot; errors:</strong> Reduce max
                concurrent extractions (try 2-4 instead of 6-8).
              </li>
              <li>
                <strong>If batch processing is too slow:</strong> Increase max concurrent
                extractions (try 6-8, but watch for errors).
              </li>
              <li>
                <strong>If PDFs hang during extraction:</strong> Increase extraction timeout (try
                30-60 seconds for very large PDFs).
              </li>
              <li>
                <strong>Error statistics:</strong> After folder scans, check extraction_error_rate
                in the scan results. If &gt; 0%, reduce max concurrent extractions.
              </li>
            </ul>
          </div>
        </Stack>
      </Card>

      {/* Status Messages */}
      {success ? (
        <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-green-300 text-sm font-medium">{success}</span>
          </Stack>
        </Card>
      ) : null}

      {error ? (
        <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300 text-sm font-medium">{error}</span>
          </Stack>
        </Card>
      ) : null}

      {/* Loading State */}
      {loading ? (
        <Card className="bg-slate-700/50 border-slate-600/50" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
            <span className="text-slate-400 text-sm">Loading Parqet settings...</span>
          </Stack>
        </Card>
      ) : null}
    </div>
  );
};

// Reason: Component is used via routing in src/routes/index.tsx, not directly imported

export { ParqetSettingsView };
